import { Badge, HStack } from "@chakra-ui/react";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { CreditFacilityExpenseEventSummaryCard } from "features/credit-facility/expenses/CreditFacilityExpenseEventSummaryCard";
import {
  getCreditFacilityExpenseDetailsTableRetrieveQueryKey,
  getCreditFacilityInterestExpenseEventsRetrieveQueryKey,
  useCreditFacilityAvailableTranchesForExpensePostingListSuspense,
  useCreditFacilityExpenseEventRetrieveSuspense,
  useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense,
  useCreditFacilityInterestExpensePost,
  useCreditFacilityLoanRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageWizardFormFooter } from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import {
  formatDateRange,
  formatIsoDateToDisplay,
} from "shared/utils/DateUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export const CreditFacilityExpenseWorkflowReview: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);

  const { fundFamilyId, creditFacilityId, expenseEventId } =
    useRequiredStringParams();

  const invalidateQueryKeys = useInvalidateQueryKeys();
  const { data: expenseEvent } = useCreditFacilityExpenseEventRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    expenseEventId,
  );

  const { data: availableTranches } =
    useCreditFacilityAvailableTranchesForExpensePostingListSuspense(
      fundFamilyId,
      creditFacilityId,
      expenseEvent.loan_id || "",
    );

  const currentTranche = availableTranches.find(
    (tranche) => tranche.id === expenseEvent.principal_tranche_id,
  );

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    expenseEvent.fund_family_event_id!,
  );

  const isDraft =
    fundFamilyEvent.state === FundFamilyEventStateMachineFieldsEnum.draft;

  const { data: loan } = useCreditFacilityLoanRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    expenseEvent.loan_id!,
  );
  const { data: adjustmentsData } =
    useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      expenseEventId,
      {
        read_only: true,
      },
    );

  const navigate = useNavigate();
  const expensePath = useMemo(
    () =>
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .creditFacilities({})
        .creditFacility({ creditFacilityId })
        .expenses({}).$,
    [fundFamilyId, creditFacilityId],
  );

  const { mutateAsync: postExpenses, isPending } =
    useCreditFacilityInterestExpensePost({
      mutation: {
        onSuccess: async () => {
          await invalidateQueryKeys(
            getCreditFacilityInterestExpenseEventsRetrieveQueryKey(
              fundFamilyId,
              creditFacilityId,
            ),
            getCreditFacilityExpenseDetailsTableRetrieveQueryKey(
              fundFamilyId,
              creditFacilityId,
            ),
          );
          navigate(expensePath);
        },
      },
    });
  const stateBadgeData = getCapitalActivityEventStateBadge(
    fundFamilyEvent.state,
    t,
  );

  const refreshExpense = async () => {
    await invalidateQueryKeys(
      getCreditFacilityInterestExpenseEventsRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
      getCreditFacilityExpenseDetailsTableRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    );
  };

  const discardExpense = async () => {
    await invalidateQueryKeys(
      getCreditFacilityInterestExpenseEventsRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
      getCreditFacilityExpenseDetailsTableRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    );
    navigate(expensePath);
  };
  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={
          currentTranche
            ? `${currentTranche.tranche_name} ${loan.name} ${t(
                "creditFacility:postExpense.dailyBreakdown",
              )}`
            : `${loan.name} ${t("creditFacility:postExpense.dailyBreakdown")}`
        }
        backButton={
          !isDraft
            ? {
                title: t("creditFacility:creditFacility"),
                path: expensePath,
              }
            : undefined
        }
        stats={<CreditFacilityExpenseEventSummaryCard />}
        badge={
          isDraft ? (
            <HStack spacing="8px">
              <Badge>
                {formatDateRange(
                  expenseEvent.period_start,
                  expenseEvent.period_end,
                )}
              </Badge>
              {stateBadgeData && (
                <Badge colorScheme={stateBadgeData.color}>
                  {stateBadgeData.text}
                </Badge>
              )}
            </HStack>
          ) : undefined
        }
      />
      <PageBody>
        {!!fundFamilyEvent.out_of_date_reason && (
          <OutOfDateAlert
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={expenseEvent.fund_family_event_id!}
            message={fundFamilyEvent.out_of_date_reason}
            onDiscard={discardExpense}
            onRefresh={refreshExpense}
          />
        )}
        <TableBox isFullHeight={true} isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {`${formatIsoDateToDisplay(
                adjustmentsData.period_start,
              )} - ${formatIsoDateToDisplay(adjustmentsData.period_end)}: ${t(
                "creditFacility:postExpense.dailyInterestExpense",
              )}`}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={adjustmentsData.table} />
          </TableBoxBody>
        </TableBox>
      </PageBody>
      {isDraft && (
        <PageWizardFormFooter
          isLoading={isPending}
          onClickNext={() =>
            postExpenses({
              fundFamilyId,
              creditFacilityId,
              expenseEventId,
            })
          }
        />
      )}
    </PageContent>
  );
};
